import React from 'react';
import { useFunnel } from '../../contexts/FunnelContext';
import type { StringKey } from '../../data/i18n';
import type { AccessInfo, FloorLevel, TriState, VerticalMethod } from '../../types/funnel';
import { digitsOnly } from '../../utils/format';
import { CarryBandPicker } from './CarryBandPicker';
import { ChoiceCard } from './ChoiceCard';
import { QuantityStepper } from './QuantityStepper';
import { Segmented } from './Segmented';
import { TextField } from './TextField';
const floors: {
  value: FloorLevel;
  key: StringKey;
  flights: number;
}[] = [{
  value: 'ground',
  key: 'flGround',
  flights: 0
}, {
  value: 'second',
  key: 'flSecond',
  flights: 1
}, {
  value: 'third',
  key: 'flThird',
  flights: 2
}, {
  value: 'fourth_plus',
  key: 'flFourth',
  flights: 3
}];
interface AccessEditorProps {
  title: string;
  access: AccessInfo;
  onChange: (patch: Partial<AccessInfo>) => void;
  multiUnit: boolean;
}
export function AccessEditor({
  title,
  access,
  onChange,
  multiUnit
}: AccessEditorProps) {
  const {
    t
  } = useFunnel();
  const tri: {
    value: TriState;
    label: string;
  }[] = [{
    value: 'yes',
    label: t('yes')
  }, {
    value: 'no',
    label: t('no')
  }, {
    value: 'unknown',
    label: t('unsure')
  }];
  const upstairs = access.floor !== 'ground';
  const elevator = upstairs && access.method !== 'stairs';
  return <section aria-label={title} className="space-y-6" data-id="element-356" data-mp="gpmrsrp8tznm">
      <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-357" data-mp="lno9p9axnsxi">{title}</h2>

      <fieldset data-id="element-358" data-mp="poqavvoeuf24">
        <legend className="mb-2 text-sm font-semibold text-ink" data-id="element-359" data-mp="po74nc84hf3x">{t('floorLabel')}</legend>
        <div role="radiogroup" aria-label={t('floorLabel')} className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-id="element-360" data-mp="uo2wkd3jpg34">
          {floors.map(f => <ChoiceCard key={f.value} size="sm" selected={access.floor === f.value} onSelect={() => onChange({
          floor: f.value,
          flights: f.flights,
          method: f.value === 'ground' ? 'stairs' : access.method
        })} title={t(f.key)} data-id="element-361" data-mp="zllbck48upj1" />)}
        </div>
      </fieldset>

      {upstairs && <Segmented<VerticalMethod> label={t('methodLabel')} value={access.method} onChange={v => onChange({
      method: v
    })} options={[{
      value: 'stairs',
      label: t('mStairs')
    }, {
      value: 'passenger_elevator',
      label: t('mPassenger')
    }, {
      value: 'freight_elevator',
      label: t('mFreight')
    }]} data-id="element-362" data-mp="2pcrmbtzfom88" />}

      {upstairs && access.method === 'stairs' && <div className="max-w-sm rounded-lg bg-ink-100" data-id="element-363" data-mp="38aluk5sqmtpk">
          <QuantityStepper label={t('flightsLabel')} value={access.flights} onChange={n => onChange({
        flights: n
      })} decLabel={t('decrease')} incLabel={t('increase')} max={20} data-id="element-364" data-mp="3m2ijk0z40nhq" />
        </div>}

      {elevator && <div className="grid gap-5 sm:grid-cols-2" data-id="element-365" data-mp="3w0y7jv2ubgn0">
          <Segmented<TriState> label={t('elevResLabel')} value={access.elevatorReservation} onChange={v => onChange({
        elevatorReservation: v
      })} options={tri} data-id="element-366" data-mp="3r1lnfi3csno4" />
          <Segmented<TriState> label={t('sharedElevLabel')} value={access.sharedElevator} onChange={v => onChange({
        sharedElevator: v
      })} options={tri} data-id="element-367" data-mp="efmyuw7zczj9" />
        </div>}

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-368" data-mp="4is3unax1h9q">
        <Segmented<TriState> label={t('truckNearLabel')} value={access.truckNearEntrance} onChange={v => onChange({
        truckNearEntrance: v
      })} options={tri} data-id="element-369" data-mp="9ieeqq39ulfd" />
        <Segmented<TriState> label={t('gateLabel')} value={access.gateAccess} onChange={v => onChange({
        gateAccess: v
      })} options={tri} data-id="element-370" data-mp="3qxuxtu22j8r2" />
      </div>

      {(multiUnit || elevator) && <Segmented<TriState> label={t('coiLabel')} value={access.coiRequired} onChange={v => onChange({
      coiRequired: v
    })} options={tri} data-id="element-371" data-mp="3hzrbivj9e7y7" />}

      <CarryBandPicker label={t('carryLabel')} value={access.carryBand} onChange={v => onChange({
      carryBand: v
    })} data-id="element-372" data-mp="3mv6m9elyznqe" />

      <div className="grid gap-4 sm:grid-cols-[160px_1fr]" data-id="element-373" data-mp="3cz3m2eaohz63">
        <TextField label={t('feetLabel')} inputMode="numeric" value={access.carryFeet} onChange={v => onChange({
        carryFeet: digitsOnly(v).slice(0, 4)
      })} data-id="element-374" data-mp="3cylg2py7s6kz" />
        <TextField label={t('accessNotesLabel')} value={access.notes} onChange={v => onChange({
        notes: v
      })} data-id="element-375" data-mp="f5jstva11m3j" />
      </div>
    </section>;
}